/**
 * Horizontally scrolling card carousel shared by blocks (offerings, case list).
 *
 * The track is a native scroll container with CSS scroll snapping. The
 * prev/next buttons move a target card index rather than scrolling relative
 * to the current position, so rapid clicks queue up whole cards even while
 * a smooth scroll is still animating.
 *
 * With `loop: false` (default) the buttons are disabled at the ends of the
 * track. With `loop: true` the full card set is cloned before and after the
 * originals, the track starts on the first real card (so the last card peeks
 * in on the left), and once scrolling settles inside a cloned region the
 * track is teleported back to the matching real card — the carousel scrolls
 * infinitely in both directions.
 *
 * Markup contract: a block root matching `blockSelector` containing
 * `.{prefix}-track` with one <article> per card, plus `.{prefix}-prev` and
 * `.{prefix}-next` buttons.
 */

export function initCarousel(blockSelector, prefix, { loop = false } = {}) {
    const blocks = document.querySelectorAll(blockSelector);
    if (!blocks.length) return;

    blocks.forEach((block) => {
        const track = block.querySelector(`.${prefix}-track`);
        const prev = block.querySelector(`.${prefix}-prev`);
        const next = block.querySelector(`.${prefix}-next`);
        if (!track || !prev || !next) return;

        let cards = track.querySelectorAll('article');
        if (!cards.length) return;

        const count = cards.length;
        const looping = loop && count > 1;

        if (looping) {
            const originals = Array.from(cards);
            originals.forEach((card) => {
                const clone = card.cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                track.appendChild(clone);
            });
            originals
                .slice()
                .reverse()
                .forEach((card) => {
                    const clone = card.cloneNode(true);
                    clone.setAttribute('aria-hidden', 'true');
                    track.insertBefore(clone, track.firstChild);
                });
            cards = track.querySelectorAll('article');
        }

        const step = () => cards[0].offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);

        // In loop mode the real cards occupy indices [count, 2 * count).
        let target = looping ? count : 0;

        if (looping) {
            track.scrollLeft = target * step();
        }

        const goTo = (index) => {
            target = Math.max(0, Math.min(cards.length - 1, index));
            track.scrollTo({ left: target * step(), behavior: 'smooth' });
        };

        const updateButtons = () => {
            if (looping) return;
            const maxScroll = track.scrollWidth - track.clientWidth;
            prev.disabled = track.scrollLeft <= 1;
            next.disabled = track.scrollLeft >= maxScroll - 1;
        };

        // Jump (without animation) from a cloned card back to the identical
        // real card so the next scroll has room in both directions.
        const normalize = () => {
            if (!looping) return;
            if (target < count) {
                target += count;
            } else if (target >= count * 2) {
                target -= count;
            } else {
                return;
            }
            track.scrollLeft = target * step();
        };

        // Re-sync the target after manual swipes once scrolling settles.
        let settleTimer;
        track.addEventListener('scroll', () => {
            updateButtons();
            clearTimeout(settleTimer);
            settleTimer = setTimeout(() => {
                target = Math.round(track.scrollLeft / step());
                normalize();
            }, 150);
        }, { passive: true });

        prev.addEventListener('click', () => goTo(target - 1));
        next.addEventListener('click', () => goTo(target + 1));
        window.addEventListener('resize', () => {
            if (looping) track.scrollLeft = target * step();
            updateButtons();
        });
        updateButtons();
    });
}
